// Bee Sweeper puzzle generator (Queens/Star-Battle variant), deterministic per level.
// One bee per colored region, per row, and per column; no two bees adjacent
// (including diagonally). Generation is fast at every size (no uniqueness search) —
// the game gives the player 3 "bee lives" to absorb any occasional ambiguity.

function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const shuffled = (arr, rng) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

// 5x5 for levels 1-25, +1 every 25 levels, capped at 15x15.
export function sizeForLevel(level) {
  return Math.min(15, 5 + Math.floor((Math.max(1, level) - 1) / 25));
}

const nbrs4 = (r, c, N) =>
  [[r - 1, c], [r + 1, c], [r, c - 1], [r, c + 1]].filter(
    ([a, b]) => a >= 0 && a < N && b >= 0 && b < N,
  );

// Place N bees: one per row, distinct columns, consecutive rows differ by >=2
// (guarantees no two bees are king-adjacent).
function placeBees(N, rng) {
  const cols = new Array(N).fill(-1);
  const used = new Array(N).fill(false);
  function bt(r) {
    if (r === N) return true;
    for (const c of shuffled([...Array(N).keys()], rng)) {
      if (used[c]) continue;
      if (r > 0 && Math.abs(c - cols[r - 1]) < 2) continue;
      cols[r] = c;
      used[c] = true;
      if (bt(r + 1)) return true;
      used[c] = false;
      cols[r] = -1;
    }
    return false;
  }
  return bt(0) ? cols : null;
}

// Grow N connected regions from the bee seeds until every cell is assigned.
// When `forcedSmall` is set (easy-start band) we first carve exact 1/2/3-cell
// regions and lock them, then fill the rest with the remaining regions.
function growRegions(N, beeCols, rng, forcedSmall) {
  const region = Array.from({ length: N }, () => new Array(N).fill(-1));
  const size = new Array(N).fill(1);
  for (let r = 0; r < N; r++) region[r][beeCols[r]] = r; // region id == row of its bee

  const locked = new Array(N).fill(false);
  const cellsOf = (rid) => {
    const out = [];
    for (let r = 0; r < N; r++) for (let c = 0; c < N; c++) if (region[r][c] === rid) out.push([r, c]);
    return out;
  };

  if (forcedSmall && N > 3) {
    const ids = shuffled([...Array(N).keys()], rng);
    for (const [rid, target] of [[ids[0], 1], [ids[1], 2], [ids[2], 3]]) {
      while (size[rid] < target) {
        const cand = [];
        for (const [r, c] of cellsOf(rid)) {
          for (const [a, b] of nbrs4(r, c, N)) if (region[a][b] === -1) cand.push([a, b]);
        }
        if (!cand.length) break;
        const [nr, nc] = shuffled(cand, rng)[0];
        region[nr][nc] = rid;
        size[rid]++;
      }
      if (size[rid] !== target) return null; // couldn't carve exact size -> retry
      locked[rid] = true;
    }
  }

  // Growth-size preference for the remaining (non-locked) regions.
  const targets = new Array(N).fill(Infinity);
  if (!forcedSmall) {
    const left = N * N - N;
    const weights = new Array(N).fill(0).map(() => 0.3 + rng() * rng() * 3);
    const wsum = weights.reduce((a, b) => a + b, 0);
    for (let i = 0; i < N; i++) targets[i] = 1 + Math.round((weights[i] / wsum) * left);
  }

  let remaining = 0;
  for (let r = 0; r < N; r++) for (let c = 0; c < N; c++) if (region[r][c] === -1) remaining++;
  let guard = 0;
  while (remaining > 0 && guard++ < N * N * 40) {
    const frontier = [];
    for (let r = 0; r < N; r++) {
      for (let c = 0; c < N; c++) {
        if (region[r][c] !== -1) continue;
        const adj = nbrs4(r, c, N).filter(([a, b]) => region[a][b] !== -1 && !locked[region[a][b]]);
        if (adj.length) frontier.push([r, c, adj]);
      }
    }
    if (!frontier.length) break;
    const [r, c, adj] = frontier[Math.floor(rng() * frontier.length)];
    const pick = shuffled(adj, rng).sort((a, b) => {
      const ra = region[a[0]][a[1]];
      const rb = region[b[0]][b[1]];
      const na = size[ra] < targets[ra] ? 0 : 1;
      const nb = size[rb] < targets[rb] ? 0 : 1;
      return na - nb || size[ra] - size[rb];
    })[0];
    const rid = region[pick[0]][pick[1]];
    region[r][c] = rid;
    size[rid]++;
    remaining--;
  }
  return remaining === 0 ? region : null;
}

// Returns { N, region: number[N][N], size: number[N], solution: Set("r-c") }
export function generatePuzzle(level) {
  const N = sizeForLevel(level);
  const easyStart = Math.max(1, level) <= 25; // first band -> guarantee small 1/2/3 regions
  const rng = mulberry32(((Math.max(1, level) + 1) * 2654435761) >>> 0);
  for (let attempt = 0; attempt < 400; attempt++) {
    const cols = placeBees(N, rng);
    if (!cols) continue;
    const region = growRegions(N, cols, rng, easyStart);
    if (!region) continue;
    const size = new Array(N).fill(0);
    for (let r = 0; r < N; r++) for (let c = 0; c < N; c++) size[region[r][c]]++;
    if (easyStart) {
      // Require a 1-cell, a 2-cell and a 3-cell region (easier foothold).
      const counts = size.reduce((m, s) => ((m[s] = (m[s] || 0) + 1), m), {});
      if (!(counts[1] && counts[2] && counts[3])) continue;
    }
    return {
      N,
      region,
      size,
      solution: new Set(cols.map((c, r) => `${r}-${c}`)),
    };
  }
  // Extremely unlikely fallback: a trivial diagonal-ish layout.
  const cols = [...Array(N).keys()];
  const region = Array.from({ length: N }, (_, r) => new Array(N).fill(r));
  return { N, region, size: new Array(N).fill(N), solution: new Set(cols.map((c, r) => `${r}-${c}`)) };
}

// Pleasant, distinguishable region palette (up to 15 regions).
export const REGION_COLORS = [
  "#fde68a", "#bfdbfe", "#fbcfe8", "#bbf7d0", "#ddd6fe",
  "#fed7aa", "#a5f3fc", "#fecaca", "#d9f99d", "#f5d0fe",
  "#c7d2fe", "#fef08a", "#99f6e4", "#e9d5ff", "#fdba74",
];
